import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { FilterOperatorType } from '../components/odata-filter/models/filter-operator-type';
import { FilterResult } from '../components/odata-filter/models/filter-result';
import {
  FilterGroup,
  ODataFilter,
  ODataFilterValues
} from '../components/odata-filter/models/odata-filter';
import { FilterOperator } from '../components/odata-filter/models/operators';
import { SelectedFilter } from '../components/odata-filter/models/selected-filter';
import { ApiVersions } from './api-version';
import { BaseService } from './base.service';
import buildQuery, { Filter } from 'odata-query';
import { CustomView } from '../components/odata-filter/models/custom-view';
import { SavedFilter } from '../components/odata-filter/models/saved-filter';
import { ViewContext } from '../components/odata-filter/models/view-context';
import { CustomViewService } from './custom-view.service';
import { InjectorModule } from '../modules/injector.module';
import { AttributeService } from './attribute.service';
import { AttributeDefinition } from '../models/attributes/attributeDefinition';
import { SearchValueType } from '../components/odata-filter/models/search-value-type';
import { FilterNameChangeMapper } from '../components/odata-filter/models/filter-name-change-mapper';
import { CustomFieldFilterModel } from '../odata-filter-models/custom-field-filter-model';
import { ChildFieldsFilterModel } from '../odata-filter-models/child-fields-filter-model';
import { WorkspaceService } from './workspace.service';
import { ComplexFieldsFilterModel } from '../odata-filter-models/complex-fields-filter-model';
import { genericErrorMessage } from './error-handler-factory.service';

@Injectable({
  providedIn: 'root'
})
export class BaseODataService extends BaseService {
  protected _baseUrl: string;
  protected _searchTextModel: ODataFilter;
  protected _filterResult: FilterResult;
  /** Incremented per request, so a response can tell whether a later one overtook it. */
  private _requestToken = 0;
  protected _chartResult: FilterResult;
  protected _filterCollection: ODataFilter[] = [];
  protected _appliedFilters: SelectedFilter[] = [];
  protected _selectedView: CustomView;
  protected _viewContext: ViewContext;
  protected _quickFilterNames: string[] = [];
  protected expand: any = [];
  private _previousLink: string = '';
  private _defaultPageSize: number = 50;
  private _orderBy: string[] = [];
  public searchText: string;
  public readonly filterResultValues = new Subject<any[]>();
  public readonly filterCollection = new BehaviorSubject<ODataFilter[]>(this._filterCollection);
  public readonly appliedFilters = new BehaviorSubject<SelectedFilter[]>(null);
  public readonly chartResultValues = new Subject<any[]>();
  public readonly quickFilterNames = new BehaviorSubject<string[]>(this._quickFilterNames);

  public isFetchingData = true;
  /** Zero-based index of the page last loaded by filter(). */
  public pageIndex = 0;

  public set orderBy(value: string[]) {
    this.setOrderBy(value);
  }
  public get orderBy(): string[] {
    return this._orderBy;
  }
  protected setOrderBy(value: string[]) {
    this._orderBy = value;
  }
  public get totalResultCount(): number {
    return this._filterResult.count;
  }
  public get filterResult(): FilterResult {
    return this._filterResult;
  }
  protected get filterUrl() {
    return `query/${this._baseUrl}`;
  }
  protected get allResultsFilterUrl() {
    const urlParams = this._baseUrl.split('/');
    if (urlParams.length > 1) {
      return `query/${urlParams[0]}MaxResult/${urlParams
        .filter((x) => x != urlParams[0])
        .join('/')}`;
    }
    return `query/${this._baseUrl}MaxResult`;
  }

  get selectedView(): CustomView {
    return this._selectedView;
  }
  protected customViewService: CustomViewService;
  protected attributeService: AttributeService;

  protected workspaceService: WorkspaceService;
  private _currentODataFilterQuery: string;

  public get currentODataFilterQuery(): string {
    return this._currentODataFilterQuery;
  }

  constructor() {
    super();
    this.customViewService = InjectorModule.injector.get(CustomViewService);
    this.attributeService = InjectorModule.injector.get(AttributeService);
    this.workspaceService = InjectorModule.injector.get(WorkspaceService);

    //removes the deleted attribute from filter collection
    this.attributeService.attributeDeleted.subscribe((att: AttributeDefinition) => {
      this.removeAttributeFilter(att);
    });

    //removes the unfiltarebale attribute from filter collection
    this.attributeService.unfilterableAttribute.subscribe((att: AttributeDefinition) => {
      this.removeAttributeFilter(att);
    });
  }

  private removeAttributeFilter(att: AttributeDefinition) {
    const index = this._filterCollection.findIndex((x) => x.targetFieldId == att.id);
    if (index != -1) {
      this._filterCollection.splice(index, 1);
      this.filterCollection.next(this._filterCollection);
    }
  }

  public get hasCertMetricsIntegration(): boolean {
    return this.appConfigService.tenant.usesCertifications;
  }

  getQueryWithNewSelectAndExpand(
    select: string[],
    expand: string[] = [],
    isApiUnversioned = false
  ) {
    const filterQueryString = this.selectedView?.isAdvanced
      ? this.getAdvancedQueryString(this.selectedView.oDataQuery, expand, this.orderBy, select)
      : buildQuery<any>({
          select: select,
          filter: this.buildFilter(this._appliedFilters),
          expand: expand
        });
    return isApiUnversioned
      ? this.allResultsFilterUrl.concat(filterQueryString)
      : this._currentODataFilterQuery.split('?')[0].concat(filterQueryString);
  }

  getChartResult(select: string[], expand: string[]) {
    this.get(
      this.getQueryWithNewSelectAndExpand(select, expand, true),
      true,
      ApiVersions.unversioned
    )
      .pipe(
        map((response) => {
          if (response && response.value) {
            return this._chartResult.parse(response);
          } else {
            return null;
          }
        })
      )
      .subscribe((data) => {
        if (data) {
          this._chartResult = data;
          this.chartResultValues.next(this._chartResult.values);
        } else {
          // request errors are swallowed into a null response upstream; emit
          // an empty result so chart consumers can settle their loading state
          this.chartResultValues.next([]);
        }
      });
  }

  applyQueryCode(query: string) {
    this._appliedFilters = [];
    this.appliedFilters.next(this._appliedFilters);

    const path = `${this.filterUrl}${this.getAdvancedQueryString(
      query,
      this.expand,
      this.orderBy,
      null
    )}`;
    this._currentODataFilterQuery = path;
    this.filter(path);
  }

  private getAdvancedQueryString(query: string, expand: any, orderBy: any, select: any) {
    const odataQueryParams = [];

    if (query && query.length) {
      odataQueryParams.push(`$filter=${query}`);
    }
    if (select) {
      odataQueryParams.push(buildQuery({ select: select }).replace('?', ''));
    }
    if (this.expand) {
      odataQueryParams.push(buildQuery({ expand: this.expand }).replace('?', ''));
    }
    if (this.orderBy && this.orderBy.length) {
      odataQueryParams.push(buildQuery({ orderBy: this.orderBy }).replace('?', ''));
    }
    return `?${odataQueryParams.join('&')}&$count=true`;
  }

  applyFilter(appliedFilters: SelectedFilter[], apiVersion: ApiVersions = ApiVersions.v1_0) {
    if (this.selectedView?.isAdvanced) {
      this.applyQueryCode(this.selectedView.oDataQuery);
      return;
    }
    if (appliedFilters) {
      this.attributeService.attributes.subscribe((attributes) => {
        if (attributes != null) {
          this._appliedFilters = [...this.validateFilters(attributes, appliedFilters)];
          this.appliedFilters.next(this._appliedFilters);
        }
      });
    }

    // Moved logic into getODataEndpoint - DRY - which will allow to separate path from execution (filtering)
    const path = this.getODataEndpoint(appliedFilters, '', ApiVersions.unversioned);

    this._currentODataFilterQuery = path;
    this.filter(path, true, apiVersion);
  }

  applyRawFilter(rawFilter: Filter<any>) {
    this._appliedFilters = [];
    this.appliedFilters.next(this._appliedFilters);

    const filter = this.searchText
      ? { and: [rawFilter, this.buildFilter([this.getSearchTextFilter(this.searchText)])[0]] }
      : rawFilter;

    const filterQueryString = buildQuery<any>({
      count: true,
      filter,
      orderBy: this.orderBy,
      expand: this.expand
    });
    const path = this.filterUrl.concat(filterQueryString);
    this._currentODataFilterQuery = path;
    this.filter(path);
  }

  gotoNextPage() {
    if (!this._filterResult.nextLink) return;
    let path = this._filterResult.nextLink.replace(this.getBaseApiUrl(ApiVersions.v1_0), '');
    this.filter(path, false);
  }
  gotoPreviousPage() {
    if (!this._previousLink) return;
    this.filter(this._previousLink, false);
  }

  applyView(view: CustomView) {
    if (!view) return;
    this.isFetchingData = true;
    this.selectView(view);
    this._quickFilterNames = view.quickFilterNames.map((x) =>
      new FilterNameChangeMapper(view.viewContext).map(x)
    );
    this.quickFilterNames.next(this._quickFilterNames);
    if (view.isAdvanced) {
      this.applyQueryCode(view.oDataQuery);
      return;
    }

    let savedAppiedFilters = SavedFilter.parseToAppliedFilters(view.filters, view.viewContext);
    this.applyFilter(savedAppiedFilters);
  }

  selectView(view: CustomView) {
    this.customViewService.selectView(view);
    this._selectedView = view;
  }

  saveView() {
    let view = new CustomView(this.selectedView);
    view.isDefault = this.selectedView.isDefault;
    view.filters = this._appliedFilters.map((x) =>
      SavedFilter.parseToString(new SelectedFilter(x))
    );
    view.quickFilterNames = this._quickFilterNames;
    this.customViewService.update(view);
  }

  applyManageFilterResultToView(result: string[]) {
    this._quickFilterNames = result;
    this.selectedView.isModified = true;
    this.quickFilterNames.next(this._quickFilterNames);
  }

  setExpandValues(expand: string[] | any) {
    this.expand = expand;
  }

  getODataEndpoint(
    filters: SelectedFilter[],
    pathPrefix: string = '',
    apiVersion: ApiVersions = ApiVersions.unversioned
  ): string {
    const filter = this.buildFilter(filters);
    let filterQueryString = buildQuery<any>({
      count: true,
      filter,
      orderBy: this.orderBy,
      expand: this.expand
    });

    const path =
      (pathPrefix !== null && pathPrefix.trim().length > 0 ? pathPrefix + '/' : '') +
      (apiVersion !== null && apiVersion !== ApiVersions.unversioned ? apiVersion + '/' : '') +
      this.filterUrl.concat(filterQueryString);

    return path;
  }

  public getFilterQueryForAutomation(appliedFilters: SelectedFilter[]) {
    const filter = appliedFilters.map((x) =>
      x.filter.buildFilter(
        x.searchValues.length > 0 ? x.searchValues : x.searchValue,
        x.filterOperator,
        x.getSubQueryFilters(true),
        true
      )
    );
    return buildQuery({
      count: true,
      filter
    });
  }

  protected getODataQuery(
    path: string,
    params: ODataQueryParams,
    apiVersion: ApiVersions = ApiVersions.v0_1
  ) {
    // odata-query treats a `count` of `null` as a filter object (typeof null === 'object'),
    // which silently discards the real `filter`. Only pass `count` when it is truthy.
    const { count, ...rest } = params;
    let filterQueryString = buildQuery<any>({ ...rest, ...(count && { count }) });
    let url = this.getBaseApiUrl(apiVersion).concat(path.concat(filterQueryString));
    return url;
  }

  protected filter(
    path: string,
    resetResultCount = true,
    apiVersion: ApiVersions = ApiVersions.v1_0
  ) {
    if (resetResultCount) {
      this._filterResult.count = null;
    }

    // Bound to this request: a service that swaps its parser (the payment report does, between
    // line items and summary) would otherwise parse a pending response with the wrong one.
    const resultParser = this._filterResult;
    const requestToken = ++this._requestToken;
    const isSuperseded = () => requestToken !== this._requestToken;

    this.get(path, true, apiVersion, true)
      .pipe(
        map((response) => {
          if (response && response.value && !isSuperseded()) {
            return resultParser.parse(response);
          } else {
            return null;
          }
        })
      )
      .subscribe(
        (data) => {
          // Don't overwrite a newer request's result, or clear the spinner it is waiting on.
          if (isSuperseded()) {
            return;
          }
          this.isFetchingData = false;
          if (data) {
            this.calculatePreviousLink(path);
            this.pageIndex = this.getPageIndex(path);
            this._filterResult = data;
            this.filterResultValues.next(this._filterResult.values);
          }
        },
        (error) => {
          if (isSuperseded()) {
            return;
          }
          this.isFetchingData = false;

          if (error?.status === 500) {
            //a 500 error should have already displayed a snack error in this.handelError
            //don't display a new snack error because the original snack will get hidden
            return;
          }

          const errorMessage = error?.status === 400 ? 'Invalid query filter' : genericErrorMessage;

          this.openSnackBar(errorMessage, false);
        }
      );
  }

  private getPageIndex(path: string): number {
    const skip = Number(/[?&]\$skip=(\d+)/.exec(path)?.[1] ?? 0);
    return Math.floor(skip / this._defaultPageSize);
  }

  private calculatePreviousLink(path: string) {
    let curentSkipNumber = +path
      .substring(path.lastIndexOf('&$skip='), path.length)
      .replace('&$skip=', '');
    let previousSkipNumber = curentSkipNumber - this._defaultPageSize;
    if (previousSkipNumber >= this._defaultPageSize) {
      this._previousLink = path.replace(
        `&$skip=${curentSkipNumber}`,
        `&$skip=${previousSkipNumber}`
      );
    } else {
      this._previousLink = path.replace(`&$skip=${curentSkipNumber}`, '');
    }
  }

  protected buildFilter(appliedFilters: SelectedFilter[]) {
    let filters = appliedFilters.slice();
    if (this.searchText) {
      var searchTextFilter = this.getSearchTextFilter(this.searchText);
      filters.push(searchTextFilter);
    }

    return filters.map((x) =>
      x.filter.buildFilter(
        x.searchValues.length > 0 ? x.searchValues : x.searchValue,
        x.filterOperator,
        x.getSubQueryFilters(false)
      )
    );
  }

  protected addFilterToCurrentFilters(newFilters: any[]) {
    return this.buildFilter([...this._appliedFilters]).concat(newFilters);
  }

  protected systemFilterIndex(filter: ODataFilter) {
    return this._filterCollection.findIndex(
      (x) => x.displayName == filter.displayName && x.filterGroup == filter.filterGroup
    );
  }
  private getSearchTextFilter(searchText: string) {
    return new SelectedFilter({
      filter: this._searchTextModel,
      filterOperator: new FilterOperator(
        'contains',
        FilterOperatorType.contains,
        FilterOperatorType.contains,
        true
      ),
      searchValue: searchText
    });
  }

  validateFilters(attributes: AttributeDefinition[], appliedFilters: SelectedFilter[]) {
    return appliedFilters.map((appliedFilter) => {
      let filter = appliedFilter.filter;
      if (
        filter.filterGroup == FilterGroup.TeamCustomFields ||
        filter.filterGroup == FilterGroup.PersonCustomFields ||
        filter.filterGroup == FilterGroup.TrackedFields
      ) {
        //checks for deleted customfield
        const attribute = attributes.find((x) => x.id == filter.targetFieldId);
        if (!attribute) {
          appliedFilter.showFieldDeletedWarning = true;
          return appliedFilter;
        }
        appliedFilter.filter.displayName = attribute.name;

        //checks for deleted or changed customfield valueList ita
        if (filter.valueType == SearchValueType.MultiValue) {
          filter.values = attributes
            .find((x) => x.id == filter.targetFieldId)
            .valueList.map((v) => new ODataFilterValues(v.id, v.itemValue));
          appliedFilter.searchValues.forEach((value) => {
            let valuelist = attributes
              .map((x) => {
                return x.valueList.filter((x) => x.id == value.searchValue);
              })
              .filter((x) => x.length);
            if (!valuelist.length) {
              appliedFilter.showFieldDeletedWarning = true;
              return appliedFilter;
            }
          });
        }
      }
      return appliedFilter;
    });
  }
  protected getODataResult(
    path: string,
    params: Partial<ODataQueryParams>,
    apiVersion: ApiVersions
  ) {
    const filterQueryString = buildQuery<any>({
      ...(params.select && { select: params.select }),
      orderBy: params.orderBy,
      expand: params.expand,
      // odata-query treats a `count` of `null` as a filter object (typeof null === 'object'),
      // which silently discards the real `filter`. Only pass `count` when it is truthy.
      ...(params.count && { count: params.count }),
      ...(params.top && { top: params.top }),
      filter: params.filter
    });

    let url = path.concat(filterQueryString);
    return this.get(url, true, apiVersion).pipe(
      map((response) => {
        if (response && response.value) {
          return response;
        } else {
          return null;
        }
      })
    );
  }

  protected getODataResultCountOnly(path: string, filter: Filter<any>, apiVersion: ApiVersions) {
    let filterQueryString = `/$count${buildQuery<any>({ filter: filter })}`;
    let url = path.concat(filterQueryString);
    return this.get(url, true, apiVersion);
  }

  protected setCustomFieldFilterCollection(
    customFieldFilterModel:
      CustomFieldFilterModel | ChildFieldsFilterModel | ComplexFieldsFilterModel
  ) {
    let index = this._filterCollection.findIndex(
      (x) => x.targetFieldId == customFieldFilterModel.targetFieldId
    );
    if (index == -1) {
      this._filterCollection.push(customFieldFilterModel);
    } else {
      this._filterCollection[index] = customFieldFilterModel;
    }
  }
}

export interface ODataQueryParams {
  select: string[];
  filter: Filter<any>;
  orderBy: string[];
  expand: string[];
  count: boolean;
  top?: number;
}
